import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';
import { transformToSelectOptions } from '~/utils/common';

import { API_ENDPOINT } from '~/constants';

import { deliveryAgencyKeys } from './keys';

import type {
  TypeDeliveryAgencyOutboundUnitSearchBoxOptions,
  TypeGetOutboundsSearchConditionsResponse,
} from '~/types/delivery-agency.types';

/**
 * [합포 단위 관리] 검색 필터 코드 조회 API
 */
export const fetchOutboundsSearchConditions = async (): Promise<TypeGetOutboundsSearchConditionsResponse['data']> => {
  const { data } = await axiosInstance.get<TypeGetOutboundsSearchConditionsResponse>(
    API_ENDPOINT.DELIVERY_AGENCY.GET_OUTBOUNDS_SEARCH_CONDITIONS
  );
  return data.data;
};

/**
 * [합포 단위 관리] 검색 필터 코드 조회 Query Hook
 */
export const useGetOutboundsSearchConditionsQuery = (options?: {
  enabled?: boolean;
  showGlobalErrorToast?: boolean;
}) => {
  return useQuery({
    queryKey: deliveryAgencyKeys.outboundsSearchConditions(),
    queryFn: fetchOutboundsSearchConditions,
    staleTime: 1000 * 60 * 30,
    enabled: options?.enabled ?? true,
    meta: { showGlobalErrorToast: options?.showGlobalErrorToast ?? true },
    select: (data): TypeDeliveryAgencyOutboundUnitSearchBoxOptions => ({
      searchKeywordTypes: transformToSelectOptions(data.searchKeywordTypes, 'code', 'description'),
      deliveryTypes: transformToSelectOptions(data.deliveryTypes, 'code', 'description'),
      courierCodes: transformToSelectOptions(data.courierCodes, 'code', 'description'),
    }),
  });
};

/**
 * [합포 단위 관리] 검색 필터 코드 조회 Mock
 *
 * @remarks
 * DevTools에서 Mock을 활성화하면 응답합니다.
 */
registerMockAPI<TypeGetOutboundsSearchConditionsResponse>({
  method: 'get',
  endpoint: '/api-delivery-agency/outbounds/search-conditions',
  label: '합포 단위 관리/검색 필터 코드',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: {
          searchKeywordTypes: [
            { code: 'OUTBOUND_CODE', description: '출고번호' },
            { code: 'CLIENT_ORDER_CODE', description: '판매처 주문번호' },
            { code: 'RECEIVER_NAME', description: '수령자명' },
            { code: 'ORDERER_NAME', description: '주문자명' },
          ],
          deliveryTypes: [
            { code: 'DAWN', description: '샛별배송' },
            { code: 'DAY', description: '하루배송' },
          ],
          courierCodes: [
            { code: 'NM', description: '넥스트마일' },
            { code: 'CJT', description: 'CJ' },
            { code: 'LTT', description: '롯데' },
          ],
        },
      },
    },
  ],
});
